import React, { useEffect, useRef, useState, useCallback, memo, useMemo } from "react";

// interfaces
import { AnimationItem, LottiePlayer } from "lottie-web/build/player/lottie_light";
import { EnterFrameEvent, LottieMarker, TRANSITION_ERROR_TYPES, TransitionPlayer } from "../interfaces";

// constants
import {
    TICK_STATES,
    TRANSITION_ANIMATION_SEGMENT,
    TRANSITION_LOADING_SEGMENT,
    TRANSITION_LOTTIE_TIMEOUT,
    TRANSITION_SUCCESS_SEGMENT_START,
    TRANSITION_SUCCESS_SEGMENT_START_AFFORDABLE,
} from "../constants";
import { ENTER_FRAME_DELAY } from "./constant";

// hooks
import { useAudioPlayer } from "@swiggy-private/checkout-core";

// utils
import { captureError, useEffectOnce } from "@swiggy-private/core-ui";
import { throttle, isEmpty } from "@swiggy-private/js-utils";
import { fetchTransitionLottie } from "../helpers";
import { getTransitionSoundUrl, getSoundMarkerConfig } from "./helpers";

// styles
import { Player } from "./styles";

// test ids
import { TICK_SCREEN_TEST_IDS } from "@swiggy-private/payment-test-ids";

const LottieAnimation = ({
    tickState,
    onDOMLoaded,
    onError,
    deliveryAddressAnotation,
    recordTransitionScreenLoadTime,
    isSwiggyBlackApplicable,
    isSwiggyBlackOtpAnimationApplicable,
    isPopCart,
    isBoltCartM3,
    isAffordableCartM2,
    isGiftingCart,
    transitionLottieConfig,
    tickScreenLottieUrl,
    onSuccessState,
    isMroFlow,
    childOrdersCount,
}: TransitionPlayer) => {
    const animationSegment = transitionLottieConfig?.animationSegment ?? TRANSITION_ANIMATION_SEGMENT;
    const loadingSegment = transitionLottieConfig?.loadingSegment ?? TRANSITION_LOADING_SEGMENT;
    const animationContainer = useRef<HTMLDivElement>(null);
    const animationInstance = useRef<AnimationItem>();
    const loadTime = useRef<number>(0);
    const domLoadedTime = useRef<number>(0);
    const imageLoadedTime = useRef<number>(0);
    const timerId = useRef<NodeJS.Timeout | null>(null);
    const soundMarkerRef = useRef<LottieMarker | null>(null);
    const hasPlayedSoundRef = useRef<boolean>(false);
    const isSuccessSegmentRef = useRef<boolean>(false);

    const { play } = useAudioPlayer();

    const [playing, setPlaying] = useState(false);
    const [totalFrames, setTotalFrames] = useState(0);
    const [domLoaded, setDomLoaded] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    const handleError = useCallback(
        (errorType?: TRANSITION_ERROR_TYPES, error?: unknown, url?: string) => {
            if (typeof onError === "function") {
                onError(errorType, error, url);
            }
        },
        [onError],
    );

    const recordLoadTime = useCallback(() => {
        if (typeof recordTransitionScreenLoadTime === "function") {
            const endTime = Date.now() - loadTime.current;
            const domLoadTime = domLoadedTime.current - loadTime.current;
            const imageLoadTime = imageLoadedTime.current - loadTime.current;
            recordTransitionScreenLoadTime(endTime, domLoadTime, imageLoadTime);
        }
    }, [recordTransitionScreenLoadTime]);

    const playSegment = useCallback((segment, loop) => {
        if (animationInstance.current) {
            animationInstance.current.loop = loop;
            animationInstance.current.playSegments(segment, true);
        }
    }, []);

    const enterFrameHandler = useCallback(
        (event: EnterFrameEvent) => {
            if (
                !isEmpty(soundMarkerRef.current) &&
                event.currentTime >= soundMarkerRef.current.tm &&
                !hasPlayedSoundRef.current
            ) {
                // play sound here
                const sound = getTransitionSoundUrl({
                    isBoltCartM3,
                    isSwiggyBlackOtpAnimationApplicable,
                    isAffordableCartM2,
                });
                play(sound ?? "");
                hasPlayedSoundRef.current = true;
                animationInstance.current?.removeEventListener("enterFrame");
            }
        },
        [isBoltCartM3, isSwiggyBlackOtpAnimationApplicable, isAffordableCartM2, play],
    );

    const throttledEnterFrameHandler = useMemo(() => {
        return throttle((event: EnterFrameEvent) => enterFrameHandler(event), ENTER_FRAME_DELAY);
    }, [enterFrameHandler]);

    const restOfAnimation = useMemo(() => {
        if (typeof transitionLottieConfig?.successSegment === "number") {
            return [transitionLottieConfig.successSegment, totalFrames];
        }
        if (isAffordableCartM2) {
            return [TRANSITION_SUCCESS_SEGMENT_START_AFFORDABLE, totalFrames];
        }
        if (transitionLottieConfig?.successSegment) {
            return [transitionLottieConfig?.successSegment, totalFrames];
        }
        return [TRANSITION_SUCCESS_SEGMENT_START, totalFrames];
    }, [isAffordableCartM2, totalFrames, transitionLottieConfig?.successSegment]);

    const fetchData = useCallback(async () => {
        try {
            const Lottie = (await import("lottie-web/build/player/lottie_light")).default as LottiePlayer;
            const lottieData = (await fetchTransitionLottie({
                onError: handleError,
                deliveryAddressAnotation,
                isSwiggyBlackApplicable,
                isSwiggyBlackOtpAnimationApplicable,
                isPopCart,
                isBoltCartM3,
                isAffordableCartM2,
                isGiftingCart,
                transitionLottieConfig,
                tickScreenLottieUrl,
                isMroFlow,
                childOrdersCount,
            })) as any; // can't type lottieData

            if (animationContainer.current) {
                animationInstance.current = Lottie.loadAnimation({
                    container: animationContainer.current,
                    renderer: "svg",
                    loop: false,
                    autoplay: false,
                    animationData: lottieData,
                    rendererSettings: {
                        hideOnTransparent: true,
                        progressiveLoad: true,
                    },
                });

                // Set sound marker based on cart type
                soundMarkerRef.current = getSoundMarkerConfig({
                    isAffordableCartM2,
                    isBoltCartM3,
                    isSwiggyBlackOtpAnimationApplicable,
                    lottieData,
                });

                animationInstance.current.addEventListener("DOMLoaded", () => {
                    setTotalFrames(animationInstance.current?.totalFrames ?? 0);
                    setDomLoaded(true);
                    if (domLoadedTime.current === 0) {
                        domLoadedTime.current = Date.now();
                    }
                });

                animationInstance.current.addEventListener("loaded_images", () => {
                    setImageLoaded(true);
                    if (imageLoadedTime.current === 0) {
                        imageLoadedTime.current = Date.now();
                    }
                });

                animationInstance.current.addEventListener("complete", () => {
                    if (!playing) {
                        setPlaying(true);
                    }
                    // Notify parent only after the success segment ends (not intro/loading) so
                    // footer strip can be shown once the success animation has completed.
                    if (isSuccessSegmentRef.current) {
                        onSuccessState?.();
                    }
                });

                animationInstance?.current?.addEventListener("error", (e) => {
                    handleError(TRANSITION_ERROR_TYPES.LOTTIE_ERROR, e);
                });

                animationInstance?.current?.addEventListener("enterFrame", throttledEnterFrameHandler);
            }
        } catch (e) {
            captureError(e);
            handleError(TRANSITION_ERROR_TYPES.FETCH_FAILED, e);
        }
    }, [
        handleError,
        deliveryAddressAnotation,
        isSwiggyBlackApplicable,
        isSwiggyBlackOtpAnimationApplicable,
        isPopCart,
        isBoltCartM3,
        isAffordableCartM2,
        throttledEnterFrameHandler,
        playing,
        isGiftingCart,
        transitionLottieConfig,
        tickScreenLottieUrl,
        onSuccessState,
        isMroFlow,
        childOrdersCount,
    ]);

    useEffectOnce(() => {
        // Initialize Lottie animation
        fetchData();

        if (loadTime.current === 0) {
            loadTime.current = Date.now();
        }

        // Cleanup
        return () => {
            if (animationInstance.current) {
                animationInstance.current.destroy();
            }
        };
    });

    useEffect(() => {
        if (playing) {
            if (tickState === TICK_STATES.LOADING) {
                isSuccessSegmentRef.current = false;
                playSegment(loadingSegment, true);
            } else if (tickState === TICK_STATES.SUCCESS) {
                isSuccessSegmentRef.current = true;
                playSegment(restOfAnimation, false);
            }
        }
    }, [playSegment, playing, tickState, restOfAnimation, loadingSegment]);

    useEffect(() => {
        // If DOMLoaded and imageLoaded are fired before the specific Timeout, then we clearTimeout and play the animation
        if (domLoaded && imageLoaded && !playing) {
            if (timerId.current) {
                clearTimeout(timerId.current);
            }
            recordLoadTime();
            playSegment(animationSegment, false);
            if (typeof onDOMLoaded === "function") {
                onDOMLoaded();
            }
        } else if (!timerId.current) {
            // If DOMLoaded and imageLoaded are not fired within the specific Timeout, then we will call onError
            timerId.current = setTimeout(() => {
                if (!(domLoaded && imageLoaded)) {
                    handleError(TRANSITION_ERROR_TYPES.TIMEOUT);
                    recordLoadTime();
                }
            }, TRANSITION_LOTTIE_TIMEOUT);
        }

        return () => {
            if (timerId.current) {
                clearTimeout(timerId.current);
            }
        };
    }, [domLoaded, handleError, imageLoaded, onDOMLoaded, playSegment, playing, recordLoadTime, animationSegment]);

    return <Player ref={animationContainer} data-testid={TICK_SCREEN_TEST_IDS.SVG} aria-hidden="true" />;
};

export default memo(LottieAnimation);
